// Session slice — current view, loaded session, load status. Spec §4.2.
//
// `folder` holds a `LoadedSession` produced by whichever loader matched
// the dropped folder; its `files` / `raw` fields are loader-specific and
// only the originating loader inspects them.

import type { StateCreator } from 'zustand';
import type { LoadedSession } from '../loaders/types';
import type { CalibrationData } from '../types';
import type { AppState } from './index';
import type { Side } from './predictions';
import type { VideoMeta } from './video';

export type View = 'load' | 'annotate';
export type LoadStatus = 'idle' | 'loading' | 'error';

export interface SessionSlice {
  currentView: View;
  /** Currently loaded session, or null on the load screen. */
  folder: LoadedSession<unknown, unknown> | null;
  folderSig: string | null;
  /** Cached id of the loader that produced `folder`. Used to resolve the FrameRenderer + save(). */
  loaderId: string | null;
  /** Which sides this loader populated — drives DualView layout (2 vs 4 panes). */
  availableSides: Side[];
  /**
   * Annotation flow for the loaded session. Platform secPose is always v2:
   * sampled image frames start from blank predictions and use carry-forward
   * seeding. v1 remains only for legacy/local loader compatibility.
   */
  annotationMode: 'v1' | 'v2';
  // Parsed-prediction metadata (fps, totalFrames, imageSize). Populated by
  // LoadView before the frame renderer mounts. The renderer's onLoad/
  // loadedmetadata callback later routes this through `video.setMeta` so
  // pixel dimensions can be cross-checked first.
  folderMeta: VideoMeta | null;
  // Stereo calibration parsed from a calibration sidecar, if provided.
  // null when no calibration file was loaded — epipolar curves are disabled.
  calibration: CalibrationData | null;
  loadStatus: LoadStatus;
  loadError: string | null;
  setView: (view: View) => void;
  setLoadedSession: (session: LoadedSession<unknown, unknown> | null) => void;
  setFolderMeta: (meta: VideoMeta | null) => void;
  setCalibration: (cal: CalibrationData | null) => void;
  setLoadStatus: (status: LoadStatus, error?: string | null) => void;
  reset: () => void;
}

export const createSessionSlice: StateCreator<
  AppState,
  [['zustand/immer', never]],
  [],
  SessionSlice
> = (set) => ({
  currentView: 'load',
  folder: null,
  folderSig: null,
  loaderId: null,
  availableSides: [],
  annotationMode: 'v1',
  folderMeta: null,
  calibration: null,
  loadStatus: 'idle',
  loadError: null,
  setView: (view) =>
    set((s) => {
      s.currentView = view;
    }),
  setLoadedSession: (session) =>
    set((s) => {
      s.folder = session;
      s.folderSig = session?.signature ?? null;
      s.loaderId = session?.loaderId ?? null;
      s.availableSides = session?.availableSides ?? [];
      s.annotationMode = session?.annotationMode ?? 'v1';
    }),
  setFolderMeta: (meta) =>
    set((s) => {
      s.folderMeta = meta;
    }),
  setCalibration: (cal) =>
    set((s) => {
      s.calibration = cal;
    }),
  setLoadStatus: (status, error = null) =>
    set((s) => {
      s.loadStatus = status;
      s.loadError = error;
    }),
  reset: () =>
    set((s) => {
      s.currentView = 'load';
      s.folder = null;
      s.folderSig = null;
      s.loaderId = null;
      s.availableSides = [];
      s.annotationMode = 'v1';
      s.folderMeta = null;
      s.calibration = null;
      s.loadStatus = 'idle';
      s.loadError = null;
    }),
});
